import ChatItem from '$tablet-app/src/app/chat/ChatItem';
import { ChatItem as ChatItemType } from '$tablet-app/src/app/chat/ChatItem/types';
import Button from '$tablet-app/src/app/components/Button';
import TextInput from '$tablet-app/src/app/components/TextInput';
import { useGetStateVariable } from '$tablet-app/src/app/state_variables';
import { StateVariableNames } from '$tablet-app/src/app/state_variables/state_variable_names';
import { color, palette, spacing } from '$tablet-app/src/app/theme';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { useRef } from 'react';
import {
  NativeSyntheticEvent,
  ScrollView,
  StyleProp,
  StyleSheet,
  TextInputKeyPressEventData,
  View,
  ViewStyle,
} from 'react-native';
import styled from 'styled-components/native';

export interface ChatSectionProps {
  chat_items?: ChatItemType[];

  chat_input?: string;

  mic_active?: boolean;

  isPixiDev?: boolean;

  onClose?(): void;
  onSubmit: () => void;
  onChangeInput: (text: string) => void;
  onPressResetScheduledMessages?: () => void;
  onSelectChatOption?: (input: string, item_idx: number) => void;
  onMicPress: () => void;
  onMicLongPress: () => void;
  onMicPressOut: () => void;
  onClearPress?: () => void;
  onSilentSend?: () => void;
}

// This contains props that are only for internal use within the Chat component
interface ChatSectionPropsExtended extends ChatSectionProps {
  style?: StyleProp<ViewStyle>;
}

const ChatSection = (props: ChatSectionPropsExtended) => {
  const {
    chat_items = [],
    chat_input = '',
    mic_active = false,
    isPixiDev,
    onSubmit,
    onChangeInput,
    onSelectChatOption,
    onMicPress,
    onMicLongPress,
    onMicPressOut,
    onSilentSend,
    onClose,

    style,
  } = props;

  const scrollViewRef = useRef<ScrollView>(null);
  const onContentSizeChange = () => {
    if (scrollViewRef?.current) {
      scrollViewRef.current.scrollToEnd({ animated: false });
    }
  };

  const onEnterKey = (
    event: NativeSyntheticEvent<TextInputKeyPressEventData>
  ) => {
    if (event.nativeEvent.key === 'Enter') {
      // Prevent default so the input does not lose focus
      event.preventDefault();
      onSubmit();
    }
  };

  const { data } = useGetStateVariable(
    StateVariableNames.ONLY_CHECKBOX_CHAT_OPTIONS
  );
  const dont_show_icons = data?.value === true;

  return (
    <Container style={style}>
      <ChatLog
        contentContainerStyle={chatLogContentPadding}
        ref={scrollViewRef}
        onContentSizeChange={onContentSizeChange}
      >
        {chat_items.map((item, idx) => (
          <ChatItem
            key={idx}
            idx={idx}
            item={item}
            dont_show_icon_options={dont_show_icons}
            onSelectOption={onSelectChatOption}
            onClose={onClose}
          />
        ))}
      </ChatLog>

      <InputSection>
        <Button
          round
          onPress={onMicPress}
          onLongPress={onMicLongPress}
          onPressOut={onMicPressOut}
          preset="secondary"
          disabled={mic_active}
        >
          <FontAwesomeIcon
            icon="microphone"
            size={25}
            color={mic_active ? palette.Pine_green : 'black'}
          />
        </Button>

        <TextInputContainer>
          <Input
            preset="secondary"
            placeholder="Add your message"
            value={chat_input}
            onChangeText={onChangeInput}
            maxLength={MAX_TEXT_LENGTH}
            onKeyPress={onEnterKey}
            textInputStyle={styles.textInputStyle}
          />
        </TextInputContainer>

        <Button
          onPress={onSubmit}
          onLongPress={onSilentSend}
          round
          icon="paper-plane"
          style={isPixiDev && styles.pixiDevBG}
        />
      </InputSection>
    </Container>
  );
};

const styles = StyleSheet.create({
  textInputStyle: {
    minWidth: 0,
  },
  pixiDevBG: {
    backgroundColor: 'rgb(255,217,65)',
  },
});

const MAX_TEXT_LENGTH = 400;

const Container = styled(View)`
  height: 100%;
  padding: ${spacing[3]}px;
  border-left-width: 1px;
  border-left-color: ${palette.Outline_blue};
  flex: 2;
`;

const ChatLog = styled(ScrollView)`
  flex-grow: 1;
`;
const chatLogContentPadding = { padding: 8 };

const InputSection = styled.View`
  margin-top: ${spacing[2]}px;
  flex-direction: row;
  width: 100%;
  max-width: 100%;
  gap: 10px;
`;

const TextInputContainer = styled.View`
  flex-grow: 1;
  flex-shrink: 1;
  background-color: ${color.white};
`;

const Input = styled(TextInput)`
  flex-grow: 1;
  flex-shrink: 1;
  overflow: scroll;
`;

export default ChatSection;
